import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { materialsAPI, subscriptionAPI } from '../services/api';
import { FileText, Link as LinkIcon, Sparkles, Download, Loader2, ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function Generator() {
  const [gradeLevel, setGradeLevel] = useState('');
  const [subject, setSubject] = useState('');
  const [materialSelections, setMaterialSelections] = useState({
    worksheet: 0,
    'exit ticket': 0,
    'review game': 0,
    project: 0,
    'brain break': 0,
    'discussion prompts': 0,
    quiz: 0,
    presentation: 0
  });
  const [textContent, setTextContent] = useState('');
  const [urls, setUrls] = useState('');
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Load profile defaults on mount
  useEffect(() => {
    loadProfileDefaults();
  }, []);

  const loadProfileDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://teacher-material-backend-production.up.railway.app/api'}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.profile) {
        if (data.profile.default_grade_level) setGradeLevel(data.profile.default_grade_level);
        if (data.profile.default_subject) setSubject(data.profile.default_subject);
      }
    } catch (error) {
      console.error('Error loading profile defaults:', error);
      // Don't show error to user - just continue with empty fields
    }
  };

  const handleGenerate = async () => {
    if (!gradeLevel || !subject) {
      setError('Please select grade level and subject');
      return;
    }

    const totalSelected = Object.values(materialSelections).reduce((a, b) => a + b, 0);
    if (totalSelected === 0) {
      setError('Please select at least one material type');
      return;
    }

    if (!textContent.trim() && !urls.trim()) {
      setError('Please provide content or URLs');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const usageResponse = await subscriptionAPI.getUsage();
      const usage = usageResponse.data.usage;
      
      if (!usage.is_unlimited && usage.remaining <= 0) {
        setError('You have reached your generation limit. Please upgrade your plan.');
        setLoading(false);
        return;
      }

      const response = await materialsAPI.generate({
        gradeLevel,
        subject,
        textContent,
        urls,
        materialSelections
      });

      setMaterials(response.data.materials || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate materials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadMaterial = (material) => {
    const content = `${material.title}\nType: ${material.type}\n\n${material.content}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${material.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  
  const downloadDOCX = async (material) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://teacher-material-backend-production.up.railway.app/api/materials/${material.id}/export/docx`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${material.title.replace(/[^a-z0-9]/gi, '_')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('DOCX download error:', error);
      alert('Failed to download DOCX');
    }
  };

  const downloadPPTX = async (material) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://teacher-material-backend-production.up.railway.app/api/materials/${material.id}/export/pptx`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${material.title.replace(/[^a-z0-9]/gi, '_')}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PPTX download error:', error);
      alert('Failed to download PPTX');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)'
    }}>
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'white',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            color: '#424242',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.borderColor = '#81c784'}
          onMouseOut={(e) => e.target.style.borderColor = '#e0e0e0'}
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <Logo size="medium" showTagline={false} />
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#2e7d32', marginBottom: '24px' }}>
            Tell us about your class
          </h2>

          {/* Material Selection */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#424242', marginBottom: '8px' }}>
              📋 What materials do you want?
            </h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Select how many of each type (max 5 total)
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {Object.keys(materialSelections).map((type) => (
                <div key={type} style={{
                  background: '#f9f9f9',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0'
                }}>
                  <label style={{
  display: 'flex',
  fontSize: '14px',
  fontWeight: '600',
  color: '#424242',
  marginBottom: '8px',
  alignItems: 'center',
  gap: '8px'
}}>
                    {type}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={() => {
                        if (materialSelections[type] > 0) {
                          setMaterialSelections({...materialSelections, [type]: materialSelections[type] - 1});
                        }
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        background: 'white',
                        border: '2px solid #e0e0e0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#424242'
                      }}
                    >
                      -
                    </button>
                    <span style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#2e7d32',
                      minWidth: '24px',
                      textAlign: 'center'
                    }}>
                      {materialSelections[type]}
                    </span>
                    <button
                      onClick={() => {
                        const total = Object.values(materialSelections).reduce((a, b) => a + b, 0);
                        if (total < 5) {
                          setMaterialSelections({...materialSelections, [type]: materialSelections[type] + 1});
                        }
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #66bb6a 0%, #42a5f5 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        fontWeight: '700',
                        color: 'white'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
{/* Live Count Message */}
            {Object.values(materialSelections).reduce((a, b) => a + b, 0) > 0 && (
              <div style={{
                marginTop: '16px',
                marginBottom: '8px',
                padding: '12px 16px',
                background: '#e3f2fd',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1976d2' }}>
                  ✨ You will generate {Object.values(materialSelections).reduce((a, b) => a + b, 0)} material{Object.values(materialSelections).reduce((a, b) => a + b, 0) === 1 ? '' : 's'}
                </span>
              </div>
            )}

            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: '#e8f5e9',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#2e7d32' }}>
                Total Materials Selected:
              </span>
              <span style={{ fontSize: '20px', fontWeight: '700', color: '#2e7d32' }}>
                {Object.values(materialSelections).reduce((a, b) => a + b, 0)} / 5
              </span>
            </div>
          </div>

          {/* Reset Button */}
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={() => setMaterialSelections({
                worksheet: 0,
                'exit ticket': 0,
                'review game': 0,
                project: 0,
                'brain break': 0,
                'discussion prompts': 0,
                quiz: 0,
                presentation: 0
              })}
              style={{
                padding: '8px 16px',
                background: 'white',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#ef5350';
                e.target.style.color = '#ef5350';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = '#e0e0e0';
                e.target.style.color = '#666';
              }}
            >
              🔄 Reset All Selections
            </button>
          </div>

          {/* Grade Level & Subject */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#424242',
                marginBottom: '8px'
              }}>
                Grade Level
              </label>
              <select
    value={gradeLevel}
    onChange={(e) => setGradeLevel(e.target.value)}
    style={{
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: '#fff',
      cursor: 'pointer'
    }}
    onFocus={(e) => e.target.style.borderColor = '#81c784'}
    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
  >
    <option value="">Select Grade Level</option>
    <option value="Elementary (K-5)">Elementary (K-5)</option>
    <option value="Middle School (6-8)">Middle School (6-8)</option>
    <option value="High School (9-12)">High School (9-12)</option>
    <option value="Special Education">Special Education</option>
  </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#424242',
                marginBottom: '8px'
              }}>
                Subject
              </label>
              <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#81c784'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                >
                  <option value="">Select Subject</option>
                  <optgroup label="Math">
                    <option value="Math">Math (General)</option>
                    <option value="Algebra">Algebra</option>
                    <option value="Geometry">Geometry</option>
                    <option value="Pre-Algebra">Pre-Algebra</option>
                    <option value="Calculus">Calculus</option>
                    <option value="Statistics">Statistics</option>
                    <option value="Trigonometry">Trigonometry</option>
                  </optgroup>
                  <optgroup label="Science">
                    <option value="Science">Science (General)</option>
                    <option value="Biology">Biology</option>
                    <option value="Chemistry">Chemistry</option>
                    <option value="Physics">Physics</option>
                    <option value="Earth Science">Earth Science</option>
                    <option value="Environmental Science">Environmental Science</option>
                  </optgroup>
                  <optgroup label="English / Language Arts">
                    <option value="English">English (General)</option>
                    <option value="Literature">Literature</option>
                    <option value="Writing">Writing</option>
                    <option value="Reading">Reading</option>
                    <option value="Grammar">Grammar</option>
                  </optgroup>
                  <optgroup label="Social Studies">
                    <option value="History">History (General)</option>
                    <option value="US History">US History</option>
                    <option value="World History">World History</option>
                    <option value="Government">Government</option>
                    <option value="Economics">Economics</option>
                    <option value="Geography">Geography</option>
                  </optgroup>
                  <optgroup label="Social Science">
                    <option value="Psychology">Psychology</option>
                    <option value="Sociology">Sociology</option>
                    <option value="Philosophy">Philosophy</option>
                  </optgroup>
                  <optgroup label="Arts">
                    <option value="Art">Art</option>
                    <option value="Music">Music</option>
                    <option value="Theater">Theater</option>
                    <option value="Photography">Photography</option>
                    <option value="Film">Film</option>
                  </optgroup>
                  <optgroup label="Health / PE">
                    <option value="Health">Health</option>
                    <option value="Physical Education">Physical Education</option>
                    <option value="Nutrition">Nutrition</option>
                  </optgroup>
                  <optgroup label="World Languages">
                    <option value="Spanish">Spanish</option>
                    <option value="French">French</option>
                    <option value="German">German</option>
                    <option value="Chinese">Chinese</option>
                    <option value="Japanese">Japanese</option>
                    <option value="Latin">Latin</option>
                  </optgroup>
                </select>
            </div>
          </div>

          {/* Quick Templates */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#424242', marginBottom: '16px' }}>
              ⚡ Quick Templates (optional)
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <button
                onClick={() => setTextContent('Create an exit ticket for today\'s lesson on [topic]. Include 3-4 quick check questions and a reflection prompt.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#81c784';
                  e.target.style.background = '#f1f8f4';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                📝 Exit Ticket
              </button>

              <button
                onClick={() => setTextContent('Create a vocabulary game for these terms: [list your terms]. Make it engaging and age-appropriate.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#64b5f6';
                  e.target.style.background = '#e3f2fd';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                🎮 Vocabulary Game
              </button>

              <button
                onClick={() => setTextContent('Create a 5-minute brain break activity that gets students moving and re-energized for learning.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#fdd835';
                  e.target.style.background = '#fff9c4';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                🧠 Brain Break
              </button>

              <button
                onClick={() => setTextContent('Create a project rubric for [project name]. Include criteria for content, creativity, presentation, and effort with clear point values.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#81c784';
                  e.target.style.background = '#f1f8f4';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                📊 Project Rubric
              </button>

              <button
                onClick={() => setTextContent('Create discussion questions for [topic/text]. Include questions at different thinking levels - recall, analysis, and evaluation.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#64b5f6';
                  e.target.style.background = '#e3f2fd';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                💬 Discussion Questions
              </button>

              <button
                onClick={() => setTextContent('Create a review game for [unit/chapter name]. Make it fun and interactive - like Jeopardy, bingo, or trivia.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#fdd835';
                  e.target.style.background = '#fff9c4';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                🎯 Review Game
              </button>
<button
                onClick={() => setTextContent('Create a worksheet for [topic]. Include a mix of multiple choice, short answer, and fill-in-the-blank questions with clear instructions.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#81c784';
                  e.target.style.background = '#f1f8f4';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                📄 Worksheet
              </button>

              <button
                onClick={() => setTextContent('Create a quiz on [topic]. Include 10-15 questions with a variety of formats (multiple choice, true/false, short answer) and an answer key.')}
                style={{
                  padding: '12px 16px',
                  background: 'white',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#64b5f6';
                  e.target.style.background = '#e3f2fd';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.background = 'white';
                }}
              >
                ✅ Quiz
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#424242',
              marginBottom: '8px',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileText size={18} />
              Content (Paste text, article, or notes)
            </label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Paste your lesson content, textbook excerpt, article, or notes here..."
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '16px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#81c784'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* URLs */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'flex',
              fontSize: '14px',
              fontWeight: '600',
              color: '#424242',
              marginBottom: '8px',
              alignItems: 'center',
              gap: '8px'
            }}>
              <LinkIcon size={18} />
              URLs (Optional - one per line)
            </label>
            <textarea
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              placeholder="https://example.com/article&#10;https://another-site.com/resource"
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '16px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'monospace'
              }}
              onFocus={(e) => e.target.style.borderColor = '#81c784'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#ffebee',
              border: '1px solid #ef5350',
              borderRadius: '8px',
              color: '#c62828',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: loading ? '#bdbdbd' : 'linear-gradient(135deg, #66bb6a 0%, #42a5f5 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              transition: 'transform 0.2s',
              boxShadow: '0 4px 12px rgba(102, 187, 106, 0.3)'
            }}
            onMouseOver={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {loading ? (
  <>
    <Loader2 size={24} className="animate-spin" />
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
        Generating amazing materials...
      </div>
      <div style={{ fontSize: '14px', fontWeight: '400', opacity: 0.9 }}>
        ⏰ This usually takes 1-2 minutes
      </div>
    </div>
  </>
            ) : (
              <>
                <Sparkles size={24} />
                Generate Materials
              </>
            )}
          </button>
        </div>

        {materials.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#2e7d32', marginBottom: '24px' }}>
              Your Generated Materials
            </h2>
            <div style={{ display: 'grid', gap: '24px' }}>
              {materials.map((material, index) => (
                <div key={index} style={{
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  padding: '24px',
                  transition: 'all 0.2s'
                }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#81c784';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(129, 199, 132, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#2e7d32', marginBottom: '8px' }}>
                        {material.title}
                      </h3>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: '#e8f5e9',
                        color: '#2e7d32',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {material.type}
                      </span>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const menu = e.currentTarget.nextElementSibling;
                          menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, #66bb6a 0%, #42a5f5 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'transform 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        <Download size={18} />
                        Download
                      </button>

                      {/* Dropdown menu */}
                      <div
                        style={{
                          display: 'none',
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '4px',
                          background: 'white',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 10,
                          minWidth: '120px'
                        }}
                      >
                        
                        <button
                          onClick={() => {
                            downloadDOCX(material);
                            document.querySelectorAll('[style*="display: block"]').forEach(el => el.style.display = 'none');
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            background: 'white',
                            border: 'none',
                            borderTop: '1px solid #e0e0e0',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#424242'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                          onMouseOut={(e) => e.target.style.background = 'white'}
                        >
                          DOCX
                        </button>
                        <button
                          onClick={() => {
                            downloadPPTX(material);
                            document.querySelectorAll('[style*="display: block"]').forEach(el => el.style.display = 'none');
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            background: 'white',
                            border: 'none',
                            borderTop: '1px solid #e0e0e0',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#424242',
                            borderBottomLeftRadius: '6px',
                            borderBottomRightRadius: '6px'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                          onMouseOut={(e) => e.target.style.background = 'white'}
                        >
                          PPTX
                        </button>
                      </div>
                    </div>
                  </div>
                <div style={{
  padding: '16px',
  background: '#f5f5f5',
  borderRadius: '8px',
  fontSize: '14px',
  lineHeight: '1.6',
  color: '#424242',
  maxHeight: '400px',
  overflowY: 'auto'
}} className="markdown-content">
  <ReactMarkdown remarkPlugins={[remarkGfm]}>{material.content}</ReactMarkdown>
</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
<Footer />
    </div>
  );
}